"use client";

import clsx from "clsx";
import { useState, useEffect } from "react";
import { FacetGroup as FacetGroupType } from "@repo/apis/single-product";
import { getStockPriceData } from "@repo/apis/stockprice";
import { Box } from "@repo/ui/box";
import { DynamicHeightSource } from "@repo/ui/dynamic-height";
import * as Flex from "@repo/ui/flex";
import { InlineMessage } from "@repo/ui/inline-message";
import { ProductPrice } from "@repo/ui/product-price";
import { ProductVariantSelector } from "@repo/ui/product-variant-select";
import { SaveForLater } from "@repo/ui/saved-items";
import { useStore } from "@repo/ui/store";
import { Text } from "@repo/ui/text";
import { ToastProvider, Toast, ToastViewport } from "@repo/ui/toast";
import { AddToBagButton } from "@/components/add-to-bag/button";
import { AddToBagSheet } from "@/components/add-to-bag/sheet";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import { ViewProductAnalytics } from "@/components/analytics/view-product-analytics";
import { FacetGroup } from "@/components/facet-group";
import {
  getBrandFromGender,
  useFloorContext,
} from "@/components/floor-context";
import { SizeGuideSheet } from "@/components/size-guide";
import { Large, Small } from "@/components/viewport";
import { ChevronIcon } from "@/theme/icons/chevron";
import { CloseIcon } from "@/theme/icons/close";
import { InfoRound } from "@/theme/icons/info-round";
import { TickIcon } from "@/theme/icons/tick";
import {
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_BODY3,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_HEADING2,
} from "@/theme/topshop/typography/typography-constants";
import {
  CatalogueMappedProduct,
  mapProductStockPriceData,
  StockPriceMappedProduct,
} from "@/utils/map-product-data";
import {
  mapSelectableVariants,
  MappedVariant,
} from "@/utils/map-selectable-variants";
import { clientRequest } from "@/utils/request-adapters/client-request";
import {
  PRODUCT_STRUCTURED_DATA_ID,
  createStructuredData,
} from "@/utils/structured-data/product";
import { ProductDetails } from "../product-details";
import classes from "./overview.module.css";

export const Overview = ({
  fullProductData,
  facetGroupData,
  currency,
}: {
  fullProductData: CatalogueMappedProduct;
  facetGroupData: FacetGroupType | null;
  currency: string;
}) => {
  const {
    variants,
    isOneSize,
    isNoSize,
    id,
    productCode,
    name,
    brand,
    productType,
    gender,
  } = fullProductData;
  const { countryData, shop, keyStoreDataversion } = useStore();
  const { pushToDataLayer } = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null,
  );
  const [mappedVariants, setMappedVariants] = useState<MappedVariant[] | []>(
    [],
  );
  const [productStockPriceData, setProductStockPriceData] =
    useState<StockPriceMappedProduct | null>(null);
  const { setFloor } = useFloorContext();

  const isKnownToBeOutOfStock = productStockPriceData?.isInStock === false;

  useEffect(() => {
    const floorFromGender = getBrandFromGender(gender);
    if (floorFromGender) {
      setFloor(floorFromGender);
    }
  }, [gender, setFloor]);

  useEffect(() => {
    getStockPriceData({
      requestInstance: clientRequest,
      productIds: id,
      store: countryData.storeCode,
      keyStoreDataversion,
      country: countryData.countryCode,
      lang: countryData.defaultLanguage,
      shop,
    }).then((data) => {
      const mappedFullProductData = mapProductStockPriceData({
        productData: fullProductData,
        stockPriceData: data[0]!,
      });
      setProductStockPriceData(mappedFullProductData);
      setMappedVariants(mapSelectableVariants(mappedFullProductData.variants));

      const script = document.getElementById(PRODUCT_STRUCTURED_DATA_ID);

      if (script?.textContent) {
        try {
          const currentData = JSON.parse(script.textContent);
          const updatedData = createStructuredData(
            mappedFullProductData,
            currentData["@graph"][0].url,
          );
          script.textContent = JSON.stringify(updatedData);
        } catch (error) {
          console.error("Failed to update structured data:", error);
        }
      }
    });
  }, [countryData, keyStoreDataversion, shop, id, fullProductData]);

  useEffect(() => {
    if (mappedVariants && (isOneSize || isNoSize)) {
      setSelectedVariantId(Number(mappedVariants[0]?.value));
    }
  }, [isOneSize, isNoSize, mappedVariants]);

  const onAddToBagClick = async () => {
    setErrorMessage(null);
  };

  const onAddToBagSuccess = (variantId: number) => {
    pushToDataLayer({
      add_to_cart: {
        ecommerce: createCommerceDataForAnalytics({ productId: id, variantId }),
      },
    });
  };

  const onAddToBagError = (message: string) => {
    setErrorMessage(message);
  };

  const handleSaveForLaterAnalytics = (params: { productId: number }) => {
    pushToDataLayer({
      add_to_wishlist: {
        ecommerce: createCommerceDataForAnalytics({
          productId: params.productId,
          variantId: selectedVariantId,
        }),
      },
    });
  };

  const createCommerceDataForAnalytics = ({
    productId,
    variantId,
  }: {
    productId: number;
    variantId?: number | null;
  }) => {
    const pricePounds = productStockPriceData!.productPrice.xrp.value;
    const priceLocal = productStockPriceData!.productPrice.current.value;
    const selectedVariant = variants.find(
      (variant) => variant.id === variantId,
    );

    return {
      currency: "GBP",
      value: pricePounds,
      localCurrencyCode: currency,
      valueLocalCurrency: priceLocal,
      items: [
        {
          item_id: selectedVariant?.sku,
          productId: `${productId}`,
          productCode: `${productCode}`,
          variantId: selectedVariant ? `${selectedVariant?.id}` : undefined,
          item_name: name,
          item_brand: brand?.name,
          item_category: productType?.name,
          item_variant: selectedVariant?.name,
          price: pricePounds,
          priceLocalCurrency: priceLocal,
          quantity: 1,
          size: selectedVariant?.brandSize,
          itemType: "Product" as const,
        },
      ],
    };
  };

  const getInStockColourWayId = () => {
    if (!mappedVariants.length || !productStockPriceData) {
      return fullProductData.variants[0]?.colourWayId;
    }

    const inStockVariant = mappedVariants.find(
      (variant) => variant.status !== "disabled",
    );
    if (inStockVariant) {
      const stockVariant = productStockPriceData.variants.find(
        (variant) => variant.id === Number(inStockVariant.value),
      );
      return (
        stockVariant?.colourWayId ?? fullProductData.variants[0]?.colourWayId
      );
    }

    return fullProductData.variants[0]?.colourWayId;
  };

  const hasOptions = !(isOneSize || isNoSize);

  const saveForLater = (
    <SaveForLater
      productId={fullProductData.id}
      colourWayId={getInStockColourWayId()}
      onAdded={handleSaveForLaterAnalytics}
      className={classes.saveForLater}
    />
  );

  const outOfStock = isKnownToBeOutOfStock && (
    <span className={clsx(classes.outOfStock, TYPOGRAPHY_BODY3)}>
      Out of stock
    </span>
  );

  const title = <h1 className={TYPOGRAPHY_HEADING2}>{fullProductData.name}</h1>;

  const price = (className: string) => (
    <div data-testid="price-container" className={classes.reserve20}>
      {productStockPriceData && (
        <ProductPrice
          priceData={productStockPriceData.productPrice}
          country="GB"
          className={className}
          currentPriceCopy="Current price"
          previousPriceCopy="Previous price"
          showDiscount
          currency={currency}
        />
      )}
    </div>
  );

  const sizeInfo = hasOptions ? (
    <Flex.Container align="start" gap="sm" justify="space-between">
      {fullProductData.info?.sizeAndFit && (
        <p
          data-testid="size-and-fit"
          className={clsx(TYPOGRAPHY_BODY3_SENTENCE, classes.sizeInfo)}
        >
          {fullProductData.info.sizeAndFit}
        </p>
      )}
      <SizeGuideSheet sizeGuideUrl={fullProductData.sizeGuide} />
    </Flex.Container>
  ) : (
    <Text className={TYPOGRAPHY_LABEL3}>{variants[0]?.brandSize}</Text>
  );

  const facetGroup = facetGroupData && (
    <FacetGroup facetGroup={facetGroupData} />
  );

  return (
    <div className={classes.overview}>
      <Small>
        <Flex.Container gap="lg" column>
          <DynamicHeightSource>
            <Flex.Container gap="lg" column>
              <Flex.Container gap="xs" column>
                {outOfStock}
                <Flex.Container gap="xs" justify="space-between">
                  <Flex.Container gap="xs" column>
                    {title}
                    {price(TYPOGRAPHY_BODY3)}
                  </Flex.Container>
                  <div className={classes.facetSmall}>{facetGroup}</div>
                </Flex.Container>
              </Flex.Container>

              <Flex.Container>
                {hasOptions && !isKnownToBeOutOfStock ? (
                  <AddToBagSheet
                    variants={mappedVariants}
                    onSuccess={onAddToBagSuccess}
                  />
                ) : (
                  <AddToBagButton
                    variantId={selectedVariantId}
                    isInStock={!isKnownToBeOutOfStock}
                    onClick={onAddToBagClick}
                    onError={onAddToBagError}
                    onSuccess={onAddToBagSuccess}
                  />
                )}
                {saveForLater}
              </Flex.Container>
            </Flex.Container>
          </DynamicHeightSource>
          {sizeInfo}
        </Flex.Container>

        {!hasOptions && (
          <ToastProvider>
            <Toast
              message={errorMessage}
              toastTitle={TYPOGRAPHY_HEADING3_BOLD}
              toastMessage={TYPOGRAPHY_BODY3_SENTENCE}
              variant="error"
              closeIcon={<CloseIcon label="Close" />}
              onClose={() => setErrorMessage(null)}
              open={!!errorMessage}
            />
            <ToastViewport />
          </ToastProvider>
        )}
      </Small>
      <Large>
        <Flex.Container gap="xl" column>
          <Flex.Container gap="2xs" column>
            <div
              data-testid="oos-container"
              className={clsx(TYPOGRAPHY_BODY3, classes.reserve20)}
            >
              {outOfStock}
            </div>
            {title}
            {price(TYPOGRAPHY_HEADING2)}
          </Flex.Container>
          {facetGroup}

          <Flex.Container gap="xs" column>
            {!isKnownToBeOutOfStock && hasOptions && (
              <ProductVariantSelector
                mappedVariants={mappedVariants}
                onSelect={(id) => {
                  setSelectedVariantId(Number(id));
                }}
                placeholder="SELECT SIZE"
                itemIndicatorContent={<TickIcon />}
                icon={<ChevronIcon />}
                selectItemClassName={TYPOGRAPHY_LABEL3}
                className={TYPOGRAPHY_LABEL3}
              />
            )}

            {sizeInfo}
          </Flex.Container>

          <Flex.Container column gap="xs">
            <Flex.Container align="center">
              <AddToBagButton
                variantId={selectedVariantId}
                isInStock={!isKnownToBeOutOfStock}
                onClick={onAddToBagClick}
                onError={onAddToBagError}
                onSuccess={onAddToBagSuccess}
              />

              {saveForLater}
            </Flex.Container>
            {errorMessage && (
              <Box paddingY="sm">
                <InlineMessage
                  variant="error"
                  icon={<InfoRound />}
                  inlineMessageClass={TYPOGRAPHY_BODY3_SENTENCE}
                >
                  {errorMessage}
                </InlineMessage>
              </Box>
            )}
          </Flex.Container>
        </Flex.Container>
      </Large>
      <ProductDetails fullProductData={fullProductData} />

      {productStockPriceData && (
        <ViewProductAnalytics mappedFullProductData={productStockPriceData} />
      )}
    </div>
  );
};
